import React, {FC, memo, useCallback} from "react";
import Picture from "./Picture";
import {ScrollDownIcon} from "./Icons";

interface HeroImageSectionProps {
    text: string;
    subtitle?: string;
    image: string;
    nextSectionId: string;
}

export const HeroImageSection: FC<HeroImageSectionProps> = memo(({
                                                                     text,
                                                                     subtitle,
                                                                     image,
                                                                     nextSectionId
                                                                 }: HeroImageSectionProps) => {

    const handleScrollToNext = useCallback(() => {
        const nextSection = document.querySelector(`#${nextSectionId}`);
        if (nextSection) {
            nextSection.scrollIntoView({behavior: 'smooth'});
        }
    }, [nextSectionId]);

    const handleImageError = useCallback((e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        const target = e.currentTarget;
        target.onerror = null;
        target.src = 'https://placehold.co/1920x1080/153D40/C6A375?text=All+Aces+Club';
    }, []);

    return (
        <div
            id="hero-section"
            className="snap-section relative h-screen flex items-center bg-black justify-center text-center overflow-hidden">
            {/* Background Image */}
            <div className="absolute inset-0">
                <Picture
                    {...(image.toLowerCase().endsWith('.webp') ? { webpSrc: image } : image.toLowerCase().endsWith('.png') ? { pngSrc: image } : { jpgSrc: image })}
                    alt="Hero Image"
                    className="w-full h-full object-cover object-center"
                    loading="eager"
                    fetchPriority="high"
                    onErrorSrc={'https://placehold.co/1920x1080/153D40/C6A375?text=All+Aces+Club'}
                />
            </div>

            {/* Gradient Overlay */}
            <div
                className="absolute inset-0 z-10 bg-gradient-to-b from-royal-pine/80 via-transparent to-royal-pine/80"/>

            {/* Main Content */}
            <div
                className="absolute flex flex-col items-center z-20 bottom-6 sm:bottom-8 lg:bottom-10 left-0 right-0">
                {/* Title */}
                <div className="text-center">
                    <h1 className="
                        text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl
                        text-ivory leading-tight font-light tracking-wide
                        px-2 sm:px-4 lg:px-0
                        mb-4 sm:mb-6 lg:mb-8
                    ">
                        {text}
                    </h1>
                    <p className="text-ivory/80 text-xs sm:text-sm md:text-base lg:text-lg px-2 sm:px-4 lg:px-0 mb-4 sm:mb-6 lg:mb-8">
                        {subtitle}
                    </p>
                </div>

                {/* Scroll Indicator */}
                <button
                    onClick={handleScrollToNext}
                    className="
                        flex flex-col items-center space-y-2 sm:space-y-3 cursor-pointer group
                        hover:opacity-75 transition-all duration-200 focus:outline-none
                        focus:ring-2 focus:ring-ivory focus:ring-opacity-50 rounded-lg p-2
                    "
                    aria-label="Scroll to next section"
                >
                    <span className="
                        text-xs sm:text-sm uppercase tracking-widest text-ivory/90
                        group-hover:text-ivory transition-colors font-medium
                    ">
                        Scroll to enter
                    </span>
                    <div className="animate-bounce group-hover:animate-pulse">
                        <ScrollDownIcon
                            className="w-6 h-6 sm:w-8 sm:h-8 text-ivory/90 group-hover:text-ivory transition-colors"/>
                    </div>
                </button>
            </div>
        </div>
    );
});

HeroImageSection.displayName = "HeroImageSection";